import { useEffect, useState } from 'react'
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded'
import EventRoundedIcon from '@mui/icons-material/EventRounded'
import {
  getFamilyToday,
  type GetFamilyTodayResult,
} from '../../services/eduFamilyService'
import {
  listFamilyBookings,
  cancelEduBooking,
  type EduBooking,
} from '../../services/eduBookingService'
import {
  listFamilyAnnouncements,
} from '../../services/eduAnnouncementService'
import {
  listFamilyFeed,
} from '../../services/eduFeedService'
import {
  listEduThreads,
} from '../../services/eduMessagingService'
import {
  listFamilyEduEvents,
  type FamilyEduEventItem,
} from '../../services/eduEventService'
import {
  listFamilyEduPtmDays,
  listFamilyEduPtmTeachers,
  type EduPtmDayItem,
  type EduPtmBookingItem,
} from '../../services/eduPtmService'
import { DEFAULT_EDU_TIMEZONE } from '../../utils/eduZonedTime'
import {
  FamilyCard,
  Button,
  StatusChip,
  ConfirmActionModal,
  type StatusPillTone,
} from '../Common'
import { mapEduServerError } from '../Education/classesBuilderHelpers'
import { useNls } from '../../nls/nlsProvider'
import { formatCalendarDate, formatWallClockTime, formatEduDate, formatEduDateTime } from '../../utils/eduFamilyFormat'
import './familyTodayTab.css'

export interface FamilyTodayTabProps {
  workspaceId: string
  learnerId: string
  frozen?: boolean
  onOpenWorkTab?: () => void
  onOpenClassTab?: (subTab: 'announcements' | 'feed' | 'messages' | 'events' | 'meetings') => void
  classCounts?: {
    unreadAnnouncements: number
    newFeedPosts: number
    unreadMessages: number
  }
}

const attendanceTone = (status: string | null): StatusPillTone => {
  switch (status) {
    case 'PRESENT':
      return 'success'
    case 'ABSENT':
      return 'danger'
    case 'LATE':
      return 'warning'
    case 'ON_LEAVE':
      return 'info'
    case 'EXCUSED':
      return 'neutral'
    default:
      return 'neutral'
  }
}

const homeworkTone = (status: string): StatusPillTone => {
  switch (status) {
    case 'APPROVED':
      return 'success'
    case 'SUBMITTED':
      return 'info'
    case 'ASSIGNED':
    case 'RETURNED':
      return 'warning'
    case 'LATE':
      return 'danger'
    default:
      return 'neutral'
  }
}

export const FamilyTodayTab = ({
  workspaceId,
  learnerId,
  frozen = false,
  onOpenWorkTab,
  onOpenClassTab,
  classCounts,
}: FamilyTodayTabProps) => {
  const { t, locale } = useNls()
  const [reloadSeq, setReloadSeq] = useState<number>(0)
  const queryKey = `${workspaceId}_${learnerId}_${reloadSeq}`

  const [internalCounts, setInternalCounts] = useState<{
    unreadAnnouncements: number
    newFeedPosts: number
    unreadMessages: number
  }>({
    unreadAnnouncements: 0,
    newFeedPosts: 0,
    unreadMessages: 0,
  })

  const [fetchState, setFetchState] = useState<{
    key: string
    data: GetFamilyTodayResult | null
    bookings: EduBooking[]
    error: string | null
  }>({
    key: '',
    data: null,
    bookings: [],
    error: null,
  })

  const [upcomingEvents, setUpcomingEvents] = useState<FamilyEduEventItem[]>([])

  interface NextPtmBookingData {
    day: EduPtmDayItem
    teacherName: string
    booking: EduPtmBookingItem
  }

  const [nextPtmBooking, setNextPtmBooking] = useState<NextPtmBookingData | null>(null)

  useEffect(() => {
    if (frozen || !workspaceId || !learnerId) return
    let active = true

    listFamilyEduPtmDays({ workspaceId, learnerId })
      .then(async (res) => {
        if (!active) return
        const days = (res?.days || []).filter((d) => d.status !== 'CANCELLED')
        if (days.length === 0) {
          if (active) setNextPtmBooking(null)
          return
        }

        const candidateBookings: NextPtmBookingData[] = []
        for (const day of days) {
          if (!active) return
          try {
            const teachersRes = await listFamilyEduPtmTeachers({
              workspaceId,
              learnerId,
              dayId: day.id,
            })
            for (const tc of teachersRes?.teachers || []) {
              if (tc.booking && tc.booking.status === 'BOOKED') {
                candidateBookings.push({
                  day,
                  teacherName: tc.teacherName,
                  booking: tc.booking,
                })
              }
            }
          } catch {
            // Ignore individual day teacher fetch error
          }
        }

        if (!active) return
        if (candidateBookings.length === 0) {
          setNextPtmBooking(null)
          return
        }

        candidateBookings.sort(
          (a, b) =>
            new Date(a.booking.slotStart).getTime() - new Date(b.booking.slotStart).getTime(),
        )
        setNextPtmBooking(candidateBookings[0])
      })
      .catch(() => {
        if (active) setNextPtmBooking(null)
      })

    return () => {
      active = false
    }
  }, [workspaceId, learnerId, frozen, reloadSeq])

  useEffect(() => {
    if (frozen || !workspaceId || !learnerId) return
    let active = true
    const now = Date.now()
    const from = new Date(now - 12 * 60 * 60 * 1000).toISOString()
    const to = new Date(now + 7 * 24 * 60 * 60 * 1000).toISOString()

    listFamilyEduEvents({
      workspaceId,
      learnerId,
      from,
      to,
    })
      .then((res) => {
        if (!active) return
        setUpcomingEvents(res?.events || [])
      })
      .catch(() => {
        if (!active) return
        setUpcomingEvents([])
      })

    return () => {
      active = false
    }
  }, [workspaceId, learnerId, frozen, reloadSeq])

  // Cancel modal state
  const [cancelModalOpen, setCancelModalOpen] = useState<boolean>(false)
  const [cancelTarget, setCancelTarget] = useState<EduBooking | null>(null)
  const [cancelMessage, setCancelMessage] = useState<{ text: string; type: 'error' | 'success' } | null>(null)
  const [actionBusy, setActionBusy] = useState<boolean>(false)

  useEffect(() => {
    if (frozen || classCounts) return
    let active = true

    Promise.all([
      listFamilyAnnouncements({ workspaceId, learnerId }).catch(() => ({ announcements: [] })),
      listFamilyFeed({ workspaceId, learnerId }).catch(() => ({ posts: [] })),
      listEduThreads({ workspaceId, learnerId }).catch(() => ({ threads: [] })),
    ]).then(([annRes, feedRes, thrRes]) => {
      if (!active) return
      setInternalCounts({
        unreadAnnouncements: (annRes.announcements || []).filter((a) => !a.readByMe).length,
        newFeedPosts: (feedRes.posts || []).filter((p) => !p.seenByMe).length,
        unreadMessages: (thrRes.threads || []).reduce(
          (sum, t) => sum + (t.unreadCountFamily || 0),
          0,
        ),
      })
    })

    return () => {
      active = false
    }
  }, [workspaceId, learnerId, frozen, classCounts])

  const counts = classCounts || internalCounts
  const totalNewFromSchool =
    (counts.unreadAnnouncements || 0) +
    (counts.newFeedPosts || 0) +
    (counts.unreadMessages || 0)

  useEffect(() => {
    if (frozen) return
    let active = true

    Promise.all([
      getFamilyToday({ workspaceId, learnerId }),
      listFamilyBookings({ workspaceId, learnerId }).catch(() => ({ bookings: [] })),
    ])
      .then(([todayRes, bookingsRes]) => {
        if (!active) return
        setFetchState({
          key: queryKey,
          data: todayRes,
          bookings: bookingsRes.bookings || [],
          error: null,
        })
      })
      .catch((err: unknown) => {
        if (!active) return
        setFetchState({
          key: queryKey,
          data: null,
          bookings: [],
          error: mapEduServerError(err, t),
        })
      })

    return () => {
      active = false
    }
  }, [workspaceId, learnerId, frozen, reloadSeq, queryKey, t])

  const error = fetchState.key === queryKey ? fetchState.error : null
  const loading = !frozen && fetchState.key !== queryKey && !error
  const data = fetchState.key === queryKey ? fetchState.data : null
  const bookings = fetchState.key === queryKey ? fetchState.bookings : []

  const handleConfirmCancel = async () => {
    if (!cancelTarget) return
    setActionBusy(true)
    setCancelMessage(null)
    try {
      const res = await cancelEduBooking({
        workspaceId,
        bookingId: cancelTarget.id,
      })
      setCancelModalOpen(false)
      setCancelTarget(null)

      if (res.isLateCancel && !res.freeOnce && !res.waived) {
        setCancelMessage({ text: t('NLSFamilyBookCancelledLateMessage'), type: 'error' })
      } else if (res.freeOnce || res.isFreeOnce) {
        setCancelMessage({ text: t('NLSFamilyBookCancelledFreeOnceMessage'), type: 'success' })
      } else {
        setCancelMessage({ text: t('NLSFamilyBookCancelledSuccessMessage'), type: 'success' })
      }
      setReloadSeq((s) => s + 1)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to cancel'
      setCancelMessage({ text: msg, type: 'error' })
    } finally {
      setActionBusy(false)
    }
  }

  if (frozen) {
    return (
      <div className="familyTodayTab">
        <FamilyCard title={t('NLSFamilyNoticeHeading')}>
          <div className="familyTodayTab__frozen">{t('NLSFamilyFrozenNotice')}</div>
        </FamilyCard>
      </div>
    )
  }

  if (loading && !data) {
    return (
      <div className="familyTodayTab">
        <div className="familyTodayTab__loading" aria-label={t('NLSFamilyAriaLoading')}>
          {t('NLSFamilyLoading')}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="familyTodayTab">
        <FamilyCard title={t('NLSFamilyErrorTitle')}>
          <div className="familyTodayTab__error">{error}</div>
          <div className="familyTodayTab__retry">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setReloadSeq((seq) => seq + 1)}
            >
              {t('NLSFamilyRetry')}
            </Button>
          </div>
        </FamilyCard>
      </div>
    )
  }

  const sessions = data?.sessions ?? []
  const homework = data?.homework ?? []

  const attendanceLabel = (status: string | null): string => {
    switch (status) {
      case 'PRESENT':
        return t('NLSEducationAttendanceStatusPresent')
      case 'ABSENT':
        return t('NLSEducationAttendanceStatusAbsent')
      case 'LATE':
        return t('NLSEducationAttendanceStatusLate')
      case 'ON_LEAVE':
        return t('NLSEducationAttendanceStatusOnLeave')
      case 'EXCUSED':
        return t('NLSEducationAttendanceStatusExcused')
      default:
        return t('NLSFamilyTodayNotMarkedYet')
    }
  }

  const homeworkLabel = (status: string): string => {
    switch (status) {
      case 'ASSIGNED':
        return t('NLSEducationHomeworkStatusAssigned')
      case 'SUBMITTED':
        return t('NLSEducationHomeworkStatusSubmitted')
      case 'LATE':
        return t('NLSEducationHomeworkStatusLate')
      case 'RETURNED':
        return t('NLSEducationHomeworkStatusReturned')
      case 'APPROVED':
        return t('NLSEducationHomeworkStatusApproved')
      default:
        return ''
    }
  }

  // Booked sessions for today and tomorrow
  const todayStr = data?.date || new Date().toISOString().slice(0, 10)
  const tomorrowStr = (() => {
    const parts = todayStr.split('-').map((p) => parseInt(p, 10))
    if (parts.length === 3 && !parts.some(isNaN)) {
      const d = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2] + 1))
      return d.toISOString().slice(0, 10)
    }
    return ''
  })()

  const bookedSessions = bookings.filter((b) => {
    if (b.status !== 'BOOKED' && b.status !== 'WAITLIST') return false
    return b.date === todayStr || b.date === tomorrowStr
  })

  const renderSchoolStrip = () => {
    if (totalNewFromSchool === 0) return null
    return (
      <div className="familyTodayTab__schoolStrip" role="region" aria-label={t('NLSFamilyNewFromSchoolTitle')}>
        <div className="familyTodayTab__schoolStripHeader">
          <SchoolRoundedIcon sx={{ fontSize: 18 }} />
          <span className="familyTodayTab__schoolStripTitle">{t('NLSFamilyNewFromSchoolTitle')}</span>
        </div>
        <div className="familyTodayTab__schoolStripChips">
          {counts.unreadAnnouncements > 0 && (
            <button
              type="button"
              className="familyTodayTab__schoolChip"
              onClick={() => onOpenClassTab?.('announcements')}
            >
              {t('NLSFamilyNewAnnouncementsCount', { count: counts.unreadAnnouncements })}
            </button>
          )}
          {counts.newFeedPosts > 0 && (
            <button
              type="button"
              className="familyTodayTab__schoolChip"
              onClick={() => onOpenClassTab?.('feed')}
            >
              {t('NLSFamilyNewFeedCount', { count: counts.newFeedPosts })}
            </button>
          )}
          {counts.unreadMessages > 0 && (
            <button
              type="button"
              className="familyTodayTab__schoolChip"
              onClick={() => onOpenClassTab?.('messages')}
            >
              {t('NLSFamilyNewMessagesCount', { count: counts.unreadMessages })}
            </button>
          )}
        </div>
      </div>
    )
  }

  const renderNextPtmBooking = () => {
    if (!nextPtmBooking) return null
    const effectiveTz = nextPtmBooking.day.timezone || DEFAULT_EDU_TIMEZONE
    const isOnline = nextPtmBooking.day.meetingType === 'ONLINE'
    const roomOrOnline = nextPtmBooking.day.location || (isOnline ? 'Online' : 'School')

    return (
      <div
        className="familyTodayTab__eventsStrip"
        role="region"
        aria-label={t('NLSFamilyPtmNextBookingTitle')}
        data-testid="family-today-next-ptm-strip"
      >
        <div
          className="familyTodayTab__eventStripCard"
          onClick={() => onOpenClassTab?.('meetings')}
          data-testid="family-today-next-ptm-card"
        >
          <div className="familyTodayTab__eventStripCardHeader">
            <span className="familyTodayTab__eventStripCardTitle" style={{ fontWeight: 600 }}>
              {t('NLSFamilyPtmNextBookingTitle')}
            </span>
            <span className="familyTodayTab__eventStripCardAnswer">
              {nextPtmBooking.teacherName}
            </span>
          </div>
          <div className="familyTodayTab__eventStripCardTime">
            {formatEduDateTime(nextPtmBooking.booking.slotStart, locale, effectiveTz)}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>
              {roomOrOnline}
            </span>
            {isOnline && (
              <Button
                variant="primary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  if (typeof window !== 'undefined') {
                    window.location.href = `/edu/ptm/join?workspaceId=${workspaceId}&bookingId=${nextPtmBooking.booking.id}`
                  }
                }}
                data-testid="today-ptm-join-btn"
              >
                {t('NLSFamilyPtmJoinMeeting')}
              </Button>
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderEventsStrip = () => {
    if (upcomingEvents.length === 0) return null
    const displayEvents = upcomingEvents.slice(0, 3)

    return (
      <div
        className="familyTodayTab__eventsStrip"
        role="region"
        aria-label={t('NLSFamilyTodayEventsTitle')}
        data-testid="family-today-events-strip"
      >
        <div className="familyTodayTab__eventsStripHeader">
          <div className="familyTodayTab__eventsStripTitleRow">
            <EventRoundedIcon sx={{ fontSize: 18 }} />
            <span className="familyTodayTab__eventsStripTitle">
              {t('NLSFamilyTodayEventsTitle')}
            </span>
          </div>
          <Button
            variant="tertiary"
            size="sm"
            onClick={() => onOpenClassTab?.('events')}
          >
            {t('NLSFamilyTodayEventsSeeAll')}
          </Button>
        </div>

        <div className="familyTodayTab__eventsStripCards">
          {displayEvents.map((ev) => (
            <div
              key={ev.id}
              className="familyTodayTab__eventStripCard"
              onClick={() => onOpenClassTab?.('events')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onOpenClassTab?.('events')
                }
              }}
              data-testid={`today-event-chip-${ev.id}`}
            >
              <div className="familyTodayTab__eventStripCardHeader">
                <span className="familyTodayTab__eventStripCardTitle">{ev.title}</span>
                {ev.status === 'CANCELLED' && (
                  <StatusChip tone="danger" size="sm">
                    {t('NLSFamilyEventCancelled')}
                  </StatusChip>
                )}
              </div>
              <div className="familyTodayTab__eventStripCardTime">
                {ev.allDay
                  ? formatEduDate(ev.startAt, locale, ev.timezone)
                  : formatEduDateTime(ev.startAt, locale, ev.timezone)}
              </div>
              <div className="familyTodayTab__eventStripCardAnswer">
                <span>
                  {ev.myRsvp === 'GOING'
                    ? t('NLSFamilyEventRsvpGoing')
                    : ev.myRsvp === 'NOT_GOING'
                      ? t('NLSFamilyEventRsvpNotGoing')
                      : ev.myRsvp === 'MAYBE'
                        ? t('NLSFamilyEventRsvpMaybe')
                        : t('NLSFamilyEventRsvpNone')}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (
    sessions.length === 0 &&
    homework.length === 0 &&
    bookedSessions.length === 0 &&
    upcomingEvents.length === 0 &&
    !nextPtmBooking
  ) {
    return (
      <div className="familyTodayTab">
        {renderSchoolStrip()}
        {renderNextPtmBooking()}
        {renderEventsStrip()}
        <FamilyCard>
          <div className="familyTodayTab__empty">{t('NLSFamilyTodayEmpty')}</div>
        </FamilyCard>
      </div>
    )
  }

  const cancelModalBody = cancelTarget?.lateCancelNow && !cancelTarget?.freeCancelOnce
    ? t('NLSFamilyBookCancelLateWarning')
    : cancelTarget?.freeCancelOnce
    ? t('NLSFamilyBookCancelFreeOnceNotice')
    : t('NLSFamilyBookCancelConfirmBody')

  return (
    <div className="familyTodayTab">
      {renderSchoolStrip()}
      {renderNextPtmBooking()}
      {renderEventsStrip()}
      {cancelMessage && (
        <div
          className={`familyTodayTab__message familyTodayTab__message--${cancelMessage.type}`}
          role="status"
        >
          {cancelMessage.text}
        </div>
      )}

      {bookedSessions.length > 0 && (
        <FamilyCard title={t('NLSFamilyBookTodayTomorrowTitle')}>
          {bookedSessions.map((b) => (
            <div
              key={b.id}
              className="familyTodayTab__row"
              data-testid={`booked-session-${b.id}`}
            >
              <div className="familyTodayTab__rowMeta">
                <div className="familyTodayTab__rowTitle">{b.title || b.seriesId}</div>
                <div className="familyTodayTab__rowTime">
                  {formatCalendarDate(b.date, locale)}
                  {b.startTime ? ` · ${formatWallClockTime(b.startTime, locale)}` : ''}
                </div>
              </div>
              <div className="familyTodayTab__rowActions">
                {b.status === 'BOOKED' ? (
                  <StatusChip tone="info" size="sm">
                    {t('NLSFamilyBookStatusBooked')}
                  </StatusChip>
                ) : (
                  <StatusChip tone="warning" size="sm">
                    {t('NLSFamilyBookStatusWaitlist', { pos: b.waitlistPosition ?? 1 })}
                  </StatusChip>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={actionBusy}
                  onClick={() => {
                    setCancelTarget(b)
                    setCancelModalOpen(true)
                  }}
                >
                  {t('NLSFamilyBookActionCancel')}
                </Button>
              </div>
            </div>
          ))}
        </FamilyCard>
      )}

      {sessions.length > 0 && (
        <FamilyCard title={t('NLSFamilyTodayAttendanceHeading')}>
          {sessions.map((session) => (
            <div key={session.sessionId || session.seriesId} className="familyTodayTab__row">
              <div className="familyTodayTab__rowMeta">
                <div className="familyTodayTab__rowTitle">{session.title}</div>
                <div className="familyTodayTab__rowTime">
                  {formatWallClockTime(session.startTime, locale)}
                </div>
              </div>
              <StatusChip tone={attendanceTone(session.attendanceStatus)} size="sm">
                {attendanceLabel(session.attendanceStatus)}
              </StatusChip>
            </div>
          ))}
        </FamilyCard>
      )}

      {sessions.length > 0 && (
        <FamilyCard title={t('NLSFamilyTodayClassesHeading')}>
          {sessions.map((session) => (
            <div key={session.sessionId || session.seriesId} className="familyTodayTab__row">
              <div className="familyTodayTab__rowMeta">
                <div className="familyTodayTab__rowTitle">{session.title}</div>
                <div className="familyTodayTab__rowTime">
                  {formatWallClockTime(session.startTime, locale)}
                </div>
                {session.substituteName && (
                  <div className="familyTodayTab__substitute">
                    {t('NLSFamilyTodaySubstitute', { name: session.substituteName })}
                  </div>
                )}
              </div>
              {session.onlineUrl && (
                <a
                  href={session.onlineUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="familyTodayTab__onlineLink"
                >
                  {t('NLSFamilyTodayJoinOnline')}
                </a>
              )}
            </div>
          ))}
        </FamilyCard>
      )}

      {homework.length > 0 && (
        <FamilyCard title={t('NLSFamilyTodayHomeworkHeading')}>
          {homework.map((hw) => (
            <div key={hw.checklistId} className="familyTodayTab__row">
              <div className="familyTodayTab__rowMeta">
                <div className="familyTodayTab__rowTitle">{hw.title}</div>
                <div className="familyTodayTab__rowTime">
                  {formatCalendarDate(hw.dueDate, locale)}
                </div>
              </div>
              <div className="familyTodayTab__rowActions">
                {homeworkLabel(hw.status) ? (
                  <StatusChip tone={homeworkTone(hw.status)} size="sm">
                    {homeworkLabel(hw.status)}
                  </StatusChip>
                ) : null}
                {onOpenWorkTab && (
                  <Button variant="secondary" size="sm" onClick={onOpenWorkTab}>
                    {t('NLSFamilyWorkHandIn')}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </FamilyCard>
      )}

      {cancelModalOpen && (
        <ConfirmActionModal
          open={cancelModalOpen}
          onClose={() => {
            setCancelModalOpen(false)
            setCancelTarget(null)
          }}
          onConfirm={handleConfirmCancel}
          title={t('NLSFamilyBookCancelTitle')}
          body={cancelModalBody}
          confirmLabel={t('NLSFamilyBookCancelConfirm')}
          cancelLabel={t('NLSCommonActionCancel')}
          danger={Boolean(cancelTarget?.lateCancelNow && !cancelTarget?.freeCancelOnce)}
          busy={actionBusy}
        />
      )}
    </div>
  )
}
